import { withDashboardLayout } from 'layouts/DashboardLayout/DashboardLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAlert } from 'context/AlertProvider';
import PageContainer from 'components/PageContainer/PageContainer';
import { Card, Dimmer, Loader, Message } from 'semantic-ui-react';
import PreparationOrdersTable from 'components/PreparationOrdersTable/PreparationOrdersTable';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import PreparationOrdersFiltersBar from 'components/PreparationOrdersFiltersBar/PreparationOrdersFiltersBar';
import { ITEMS_PER_PAGE } from 'constants/constants';
import PendingManifestKPI from 'components/PendingManifestKPI/PendingManifestKPI';
import { useUnmanifestedShipments } from 'hooks/useUnmanifestedShipments';
import useFiltersReducer, {
  loadMoreAction,
  updateFiltersAction,
} from 'hooks/useFiltersReducer';
import { useResetSelectedRows } from 'hooks/useResetSelectedRows';
import {
  isSFPStock,
  isStock,
} from 'modules/user-has-permission/user-has-permission';
import TransferPreparationOrdersToWarehouse from 'components/TransferPreparationOrdersToWarehouse/TransferPreparationOrdersToWarehouse';
import { getAll } from 'services/preparation-orders.service';
import { useGroupIdFilters } from 'hooks/useGroupIdFilter';
import { useLocalFilteredResults } from 'hooks/useLocalFilteredResults';
import './preparation-orders-page.scss';

function PreparationOrdersPage() {
  const alert = useAlert();
  const [preparationOrders, setPreparationOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [hasNextPage, setHasNextPage] = useState(true);
  const [state, dispatch] = useFiltersReducer({
    agency_id: [],
    status: [],
    warehouse_id: '',
    is_marketplace: false,
    marketplace_id: '',
    page: 1,
  });
  const { unmanifestedShipments, fetchUnmanifestedShipments } =
    useUnmanifestedShipments();
  const { lastResetSelectedRows, resetSelectedRows } = useResetSelectedRows();
  const [selectedPreparationOrders, setSelectedPreparationOrders] = useState(
    [],
  );
  const canTransferToWarehouse = useMemo(() => isStock() && !isSFPStock(), []);
  const groupIdFilters = useGroupIdFilters();
  const fetchOrders = useCallback(
    async (state) => {
      try {
        setLoading(true);
        const { data: preparationOrders } = await getAll({
          agency_id: state.agency_id,
          warehouse_id: state.warehouse_id,
          status: state.status,
          groupIdFilters,
          is_marketplace: state.is_marketplace,
          marketplace_id: state.marketplace_id,
          page: state.page,
          itemsPerPage: ITEMS_PER_PAGE,
        });
        setHasNextPage(preparationOrders?.length === ITEMS_PER_PAGE);
        setPreparationOrders((prev) =>
          state.page > 1 ? [...prev, ...preparationOrders] : preparationOrders,
        );
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [alert, groupIdFilters],
  );

  const filteredPreparationOrders = useLocalFilteredResults(
    preparationOrders,
    query,
  );

  const updateFilters = useCallback(
    (filters) => {
      dispatch(updateFiltersAction(filters));
    },
    [dispatch],
  );

  useEffect(() => {
    (async () => {
      if (!!state?.status?.length && !(isStock() && !groupIdFilters))
        await fetchOrders(state);
    })();
  }, [state, fetchOrders, groupIdFilters]);

  return (
    <PageContainer
      title="Órdenes de preparación"
      className="preparation-orders-page"
    >
      <Dimmer active={loading && state.page === 1} inverted>
        <Loader>Recuperando órdenes…</Loader>
      </Dimmer>
      <>
        <Card.Header>
          <PreparationOrdersFiltersBar
            onFiltersChange={updateFilters}
            query={query}
            onChangeQuery={(query) => {
              setQuery(query);
              resetSelectedRows();
            }}
          />
        </Card.Header>
        <Card.Content>
          <header className="preparation-orders-page__table-header">
            {canTransferToWarehouse && (
              <TransferPreparationOrdersToWarehouse
                preparationOrders={selectedPreparationOrders}
                onTransfer={async () => {
                  resetSelectedRows();
                  await fetchOrders(state);
                }}
              />
            )}
          </header>
          {!!filteredPreparationOrders.length ? (
            <>
              <PreparationOrdersTable
                loading={loading}
                selectable={canTransferToWarehouse}
                preparationOrders={filteredPreparationOrders}
                lastResetSelectedRows={lastResetSelectedRows}
                onRowSelect={(selectedPreparationOrders) =>
                  setSelectedPreparationOrders(selectedPreparationOrders)
                }
                onLoadMoreItems={async (skip) => {
                  /**
                   * Dependiendo del caso de uso deberemos cortar la primera carga o no.
                   * En casos como este debemos evitar la primera petición que provoca la paginación y dejar que se pida
                   * por useEffect pero en otros componentes donde no haya más accionador que la paginación, necesitaremos
                   * que la primera la provoque la paginación.
                   */
                  const isFirstLoad = skip === 0;
                  if (isFirstLoad) return;
                  dispatch(loadMoreAction());
                }}
                hasNextPage={hasNextPage}
              />
              <footer className="preparation-orders-page__table-footer">
                <small>
                  {!!selectedPreparationOrders?.length &&
                  selectedPreparationOrders.length === 1
                    ? '1 seleccionada'
                    : `${selectedPreparationOrders.length} seleccionadas`}
                </small>
                <small>{preparationOrders.length} items</small>
              </footer>
            </>
          ) : (
            <Message info>
              No hay órdenes de preparación que mostrar para los criterios de
              filtrado seleccionado…
            </Message>
          )}
        </Card.Content>
        <PendingManifestKPI
          unmanifestedShipments={unmanifestedShipments}
          fetchUnmanifestedShipments={fetchUnmanifestedShipments}
        />
      </>
    </PageContainer>
  );
}

export default withDashboardLayout(PreparationOrdersPage);
